import moment, { isMoment, Moment } from 'moment';
import { cs, sk, es, hu, ru, uk, enGB, de, fr } from 'date-fns/locale';
import { Languages } from '../Enums/Common';

export const getOneWeekBeforeDate = (date: Date) => {
	return new Date().setDate(date.getDate() - 7);
};

export const getNextDateAfterDate = (date: Date) => {
	return new Date().setDate(date.getDate() + 1);
};

export const getMomentDate = (
	date: Date | Moment | string | number | null | undefined,
) => (isMoment(date) ? date : moment(date));

export const getDateLocale = (language: Languages) => {
	const LANGUAGE_LOCALES = {
		[Languages.CS]: cs,
		[Languages.SK]: sk,
		[Languages.HU]: hu,
		[Languages.RU]: ru,
		[Languages.UK]: uk,
		[Languages.ES]: es,
		[Languages.EN]: enGB,
		[Languages.DE]: de,
		[Languages.FR]: fr,
	};

	return LANGUAGE_LOCALES[language];
};

/**
 * Returns a moment object with the correct DST adjustment
 * @param {string} originDate
 * @param {string} currentDate
 * @param {string} time
 * @returns {Moment}
 */
export const getDateTimeWithDst = (
	originDate: string,
	currentDate: string,
	time: string,
	originTime: string,
): Moment => {
	const momentOrigin = getLocalDateTimeFromUtc(originDate, originTime);
	const momentCurrent = getLocalDateTimeFromUtc(currentDate, time);

	if (isDstObserved(momentOrigin) !== isDstObserved(momentCurrent)) {
		// Checks if the date is in DST
		const adjustment = isDstObserved(momentOrigin) ? 1 : -1; // Add 1 hour if DST is observed, otherwise subtract 1 hour
		return getLocalDateTimeFromUtc(currentDate, time, adjustment); // Adjust the timezone in time to match the DST
	}

	return momentCurrent;
};

/**
 * Determines if the date is in DST
 * @param {Moment} date
 * @returns {boolean}
 */
export const adjustDstBreakPoint = (date: Moment): Moment => {
	const dayBefore = moment(date).clone().subtract(1, 'days');
	const dayAfter = moment(date).clone().add(1, 'days');

	const offsetBefore = dayBefore.utcOffset();
	const offsetCurrent = date.utcOffset();
	const offsetAfter = dayAfter.utcOffset();

	if (offsetBefore !== offsetCurrent && offsetCurrent === offsetAfter) {
		// Checks if the date is DST break point, specific date when DST starts or ends
		if (offsetCurrent > offsetBefore) {
			// If the offset is greater than the day before, subtract 1 hour
			date.subtract(1, 'hour');
		} else {
			date.add(1, 'hour');
		}
	}

	return date;
};

/**
 * Gets the local date time from a UTC date string
 * @param {string} utcDate
 * @param {string} timeWithZone
 * @param {string} adjustment
 * @returns {Moment}
 */
export const getLocalDateTimeFromUtc = (
	utcDate: string,
	timeWithZone: string,
	adjustment?: number,
): Moment => {
	const [time, offset] = timeWithZone.split(/(?=[+-])/);
	let adjustedTimeWithTimezone = null;
	if (!offset) {
		console.error('Invalid time zone format');

		return moment(`${moment(utcDate).format('YYYY-MM-DD')}T${timeWithZone}`);
	}

	if (adjustment) {
		let hours = parseInt(offset);
		hours += adjustment;
		adjustedTimeWithTimezone = `${time}${hours >= 0 ? '+' : '-'}${
			Math.abs(hours) > 9 ? '' : '0'
		}${hours}`; // Adjust the timezone in time to match the DST
	}

	return moment(
		`${moment(utcDate).utcOffset(timeWithZone).format('YYYY-MM-DD')}T${
			adjustedTimeWithTimezone || timeWithZone
		}`,
	);
};

/**
 * Determines if the date is in DST
 * @param {Moment} date
 * @returns {boolean}
 */
const isDstObserved = (date: Moment): boolean => {
	const year = moment(date).year();
	const dstOffset = Math.max(
		moment([year, 0]).utcOffset(),
		moment([year, 6]).utcOffset(),
	); // Get the maximum offset for the year

	return date.utcOffset() < dstOffset; // If the offset is less than the DST offset, then the date is in DST
};
